import { useState, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, ArrowLeft, RotateCcw, Palette } from "lucide-react";

const COLORING_PALETTE = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6",
  "#8b5cf6", "#ec4899", "#14b8a6", "#a855f7", "#f59e0b",
  "#6366f1", "#d946ef",
];

interface ColoringShape {
  id: string;
  name: string;
  emoji: string;
  paths: { d: string; id: string }[];
  viewBox: string;
}

const SHAPES: ColoringShape[] = [
  {
    id: "house",
    name: "بيت جميل",
    emoji: "🏠",
    viewBox: "0 0 200 200",
    paths: [
      { id: "roof", d: "M100 20 L180 80 L20 80 Z" },
      { id: "wall", d: "M40 80 L160 80 L160 180 L40 180 Z" },
      { id: "door", d: "M80 130 L120 130 L120 180 L80 180 Z" },
      { id: "window1", d: "M55 95 L75 95 L75 120 L55 120 Z" },
      { id: "window2", d: "M125 95 L145 95 L145 120 L125 120 Z" },
      { id: "chimney", d: "M140 30 L160 30 L160 65 L140 65 Z" },
    ],
  },
  {
    id: "flower",
    name: "وردة جميلة",
    emoji: "🌸",
    viewBox: "0 0 200 200",
    paths: [
      { id: "petal1", d: "M100 60 Q120 30 100 10 Q80 30 100 60" },
      { id: "petal2", d: "M100 60 Q130 50 140 30 Q120 50 100 60" },
      { id: "petal3", d: "M100 60 Q130 70 150 60 Q130 80 100 60" },
      { id: "petal4", d: "M100 60 Q120 90 100 100 Q80 90 100 60" },
      { id: "petal5", d: "M100 60 Q70 70 60 60 Q70 50 100 60" },
      { id: "petal6", d: "M100 60 Q70 50 60 30 Q80 50 100 60" },
      { id: "center", d: "M100 60 m-12 0 a12 12 0 1 0 24 0 a12 12 0 1 0 -24 0" },
      { id: "stem", d: "M97 100 L97 180 L103 180 L103 100 Z" },
      { id: "leaf1", d: "M97 130 Q70 110 60 130 Q80 140 97 130" },
      { id: "leaf2", d: "M103 150 Q130 130 140 150 Q120 160 103 150" },
    ],
  },
  {
    id: "butterfly",
    name: "فراشة ملونة",
    emoji: "🦋",
    viewBox: "0 0 200 200",
    paths: [
      { id: "wing_tl", d: "M100 90 Q60 40 30 70 Q50 100 100 90" },
      { id: "wing_tr", d: "M100 90 Q140 40 170 70 Q150 100 100 90" },
      { id: "wing_bl", d: "M100 110 Q60 140 40 130 Q60 100 100 110" },
      { id: "wing_br", d: "M100 110 Q140 140 160 130 Q140 100 100 110" },
      { id: "body", d: "M97 70 L103 70 L103 150 L97 150 Z" },
      { id: "head", d: "M100 70 m-8 0 a8 8 0 1 0 16 0 a8 8 0 1 0 -16 0" },
      { id: "dot1", d: "M70 75 m-5 0 a5 5 0 1 0 10 0 a5 5 0 1 0 -10 0" },
      { id: "dot2", d: "M130 75 m-5 0 a5 5 0 1 0 10 0 a5 5 0 1 0 -10 0" },
    ],
  },
  {
    id: "sun_scene",
    name: "منظر طبيعي",
    emoji: "🌅",
    viewBox: "0 0 200 200",
    paths: [
      { id: "sky", d: "M0 0 L200 0 L200 120 L0 120 Z" },
      { id: "sun", d: "M150 40 m-20 0 a20 20 0 1 0 40 0 a20 20 0 1 0 -40 0" },
      { id: "mountain1", d: "M0 120 L60 50 L120 120 Z" },
      { id: "mountain2", d: "M80 120 L140 60 L200 120 Z" },
      { id: "ground", d: "M0 120 L200 120 L200 200 L0 200 Z" },
      { id: "tree_trunk", d: "M35 120 L45 120 L45 160 L35 160 Z" },
      { id: "tree_leaves", d: "M40 120 m-20 0 a20 25 0 1 0 40 0 a20 25 0 1 0 -40 0" },
    ],
  },
];

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

export default function ColoringActivity({ onComplete, onBack }: Props) {
  const [shapeIdx, setShapeIdx] = useState<number | null>(null);
  const [selectedColor, setSelectedColor] = useState(COLORING_PALETTE[0]);
  const [colorMap, setColorMap] = useState<Record<string, string>>({});
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState({ score: 0, stars: 0 });

  const shape = shapeIdx !== null ? SHAPES[shapeIdx] : null;

  const handleFill = (pathId: string) => {
    setColorMap(prev => ({ ...prev, [pathId]: selectedColor }));
  };

  const handleReset = () => {
    setColorMap({});
  };

  const handleFinish = () => {
    if (!shape) return;
    const filledCount = shape.paths.filter(p => colorMap[p.id]).length;
    const pct = filledCount / shape.paths.length;
    const score = Math.min(10, Math.round(pct * 10));
    const stars = pct >= 0.8 ? 3 : pct >= 0.5 ? 2 : 1;
    setResult({ score, stars });
    setFinished(true);
    onComplete(score, stars, "creative_coloring");
  };

  if (finished) {
    return (
      <Card className="text-center border-2 border-pink-300 bg-gradient-to-b from-pink-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">🎨</div>
          <h2 className="text-2xl font-bold text-foreground">تلوين رائع!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= result.stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">حصلت على {result.score} نقاط</p>
          <Button onClick={onBack} variant="outline">
            <ArrowLeft className="h-4 w-4 ml-2" /> العودة للأنشطة
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (shapeIdx === null) {
    return (
      <div className="space-y-4">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Card className="border-2 border-pink-200">
          <CardHeader className="text-center">
            <div className="text-5xl mb-2">🖍️</div>
            <CardTitle className="text-xl">اختر رسمة للتلوين!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {SHAPES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => { setShapeIdx(i); setColorMap({}); }}
                  className="p-4 rounded-xl border-2 border-muted hover:border-pink-400 bg-card hover:bg-pink-50 transition-all text-center"
                >
                  <div className="text-4xl mb-2">{s.emoji}</div>
                  <p className="font-medium text-foreground">{s.name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button onClick={() => setShapeIdx(null)} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <span className="text-lg font-bold text-foreground">{shape!.emoji} {shape!.name}</span>
      </div>

      <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white">
        <CardContent className="p-4 space-y-4">
          {/* Color palette */}
          <div className="flex flex-wrap gap-2 justify-center p-2 rounded-xl bg-card border">
            <Palette className="h-5 w-5 text-muted-foreground" />
            {COLORING_PALETTE.map(color => (
              <button
                key={color}
                onClick={() => setSelectedColor(color)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  selectedColor === color ? "border-foreground scale-110 shadow-md" : "border-transparent"
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          {/* SVG coloring area */}
          <div className="flex justify-center">
            <svg viewBox={shape!.viewBox} className="w-full max-w-xs h-auto border-2 border-muted rounded-xl bg-white cursor-pointer">
              {shape!.paths.map(p => (
                <path
                  key={p.id}
                  d={p.d}
                  fill={colorMap[p.id] || "#f5f5f5"}
                  stroke="#333"
                  strokeWidth="1.5"
                  onClick={() => handleFill(p.id)}
                  className="transition-colors hover:opacity-80 cursor-pointer"
                />
              ))}
            </svg>
          </div>

          <p className="text-sm text-center text-muted-foreground">
            اضغط على أي جزء من الرسمة لتلوينه! 🎨
          </p>

          <div className="flex gap-2">
            <Button onClick={handleReset} variant="outline" className="flex-1">
              <RotateCcw className="h-4 w-4 ml-1" /> مسح الألوان
            </Button>
            <Button onClick={handleFinish} className="flex-1" disabled={Object.keys(colorMap).length === 0}>
              تم! أنهيت التلوين ✨
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
